import * as React from 'react';

import {
  CustomPriceListSortOption,
  enumCustomPriceListSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
  Toggle,
  ToggleButtonGroup,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as detailState from '../../_state/customPriceListDetail.state';
import * as state from '../../_state/customPriceListsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  CustomPriceListRecord,
  CustomPriceListStatus,
} from '../../types/pricing';

import { CustomPriceListDraftDialog } from './CustomPriceListDraftDialog';
import { DuplicateCustomPriceListDialog } from './DuplicateCustomPriceListDialog';
import {
  computeStatus,
  useCustomPriceListColumns,
} from './hooks/useCustomPriceListColumns';
import { useDuplicateCustomPriceList } from './hooks/useDuplicateCustomPriceList';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: CustomPriceListSortOption; desc: CustomPriceListSortOption }
> = {
  name: {
    asc: enumCustomPriceListSortOption.NameAsc,
    desc: enumCustomPriceListSortOption.NameDesc,
  },
  operationType: {
    asc: enumCustomPriceListSortOption.OperationTypeAsc,
    desc: enumCustomPriceListSortOption.OperationTypeDesc,
  },
};

type StatusTab = 'All' | CustomPriceListStatus;

//
// ⚛️ ⚛️ ⚛️ CUSTOM PRICE LIST LIST VIEW ⚛️ ⚛️ ⚛️
//
export const CustomPriceListView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, setLocation] = useModuleLocation();

  // Atoms
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [statusFilter, setStatusFilter] = useAtom(state.$statusFilter);
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteCustomPriceLists,
  );
  const { data: statusCounts } = useAtomValue(state.$remoteStatusCounts);
  const archiveAction = useSetAtom(detailState.$archiveCustomPriceList);

  const handleDuplicateSuccess = React.useCallback(
    (newId: string) =>
      setLocation('/pricing/lists/:id' as const, { params: { id: newId } }),
    [setLocation],
  );
  const duplicate = useDuplicateCustomPriceList(handleDuplicateSuccess);

  // Data hooks
  const toast = useToast();

  // Memoized values
  const allLists = data?.customPriceLists ?? [];
  const serverTotal = data?.totalCount ?? 0;

  const activeTab: StatusTab = (statusFilter as StatusTab) ?? 'All';

  const needsClientFilter = activeTab === 'Active' || activeTab === 'Expired';
  const filteredLists = React.useMemo(() => {
    if (!needsClientFilter) return allLists;
    return allLists.filter((list) => computeStatus(list) === activeTab);
  }, [allLists, activeTab, needsClientFilter]);
  const total = serverTotal;

  const totalCount = statusCounts
    ? statusCounts.active +
      statusCounts.expired +
      statusCounts.archived +
      statusCounts.draft
    : 0;
  const hasAnyLists = totalCount > 0;

  const tabs: { key: StatusTab; label: string; count?: number }[] =
    React.useMemo(
      () => [
        {
          key: 'All',
          label: t.pricing.customPriceLists.list.statusTabs.all,
          count: statusCounts
            ? statusCounts.active +
              statusCounts.expired +
              statusCounts.archived +
              statusCounts.draft
            : undefined,
        },
        {
          key: 'Active',
          label: t.pricing.customPriceLists.list.statusTabs.active,
          count: statusCounts?.active,
        },
        {
          key: 'Expired',
          label: t.pricing.customPriceLists.list.statusTabs.expired,
          count: statusCounts?.expired,
        },
        {
          key: 'Archived',
          label: t.pricing.customPriceLists.list.statusTabs.archived,
          count: statusCounts?.archived,
        },
        {
          key: 'Draft',
          label: t.pricing.customPriceLists.list.statusTabs.draft,
          count: statusCounts?.draft,
        },
      ],
      [t, statusCounts],
    );

  const activeTabLabel =
    tabs.find((tab) => tab.key === activeTab)?.label ?? activeTab;

  const emptyPanelProps = React.useMemo(
    () =>
      hasAnyLists && activeTab !== 'All'
        ? {
            title:
              t.pricing.customPriceLists.list.empty.emptyTab(activeTabLabel),
            centered: true,
          }
        : {
            title: t.pricing.customPriceLists.list.empty.title,
            description: (
              <span>{t.pricing.customPriceLists.list.empty.description}</span>
            ),
            action: (
              <Button
                onPress={() => setIsCreateDialogOpen(true)}
                variant="solid"
                color="primary"
                icon={<LucidePlusCircle />}
              >
                {t.pricing.customPriceLists.list.createButton}
              </Button>
            ),
            centered: true,
          },
    [t, setIsCreateDialogOpen, hasAnyLists, activeTab, activeTabLabel],
  );

  // Callbacks
  const handleRowClick = React.useCallback(
    (row: CustomPriceListRecord) => {
      setLocation('/pricing/lists/:id' as const, {
        params: { id: row.id },
      });
    },
    [setLocation],
  );

  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumCustomPriceListSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const handleArchiveRow = React.useCallback(
    async (row: CustomPriceListRecord) => {
      try {
        await archiveAction({ id: row.id });
        toast.success(t.pricing.customPriceLists.toast.archived);
        refetch?.();
      } catch (err) {
        toast.error(
          t.pricing.customPriceLists.toast.archiveError(
            err instanceof Error ? err.message : t.common.errors.unknown,
          ),
        );
      }
    },
    [archiveAction, toast, t, refetch],
  );

  const handleDuplicateRow = React.useCallback(
    (row: CustomPriceListRecord) => {
      duplicate.open(row.id, row.name);
    },
    [duplicate],
  );

  const handleTabChange = React.useCallback(
    (tab: StatusTab) => {
      setStatusFilter(tab === 'All' ? ('' as CustomPriceListStatus) : tab);
      setOffset(0);
    },
    [setStatusFilter, setOffset],
  );

  const { columns, columnVisibilityState } = useCustomPriceListColumns({
    onRowClick: handleRowClick,
    onArchive: handleArchiveRow,
    onDuplicate: handleDuplicateRow,
  });

  return (
    <>
      <ListScreenLayout
        className="@CustomPriceListView"
        filters={
          hasAnyLists ? (
            <ToggleButtonGroup
              selectionMode="single"
              selectedKeys={new Set([activeTab])}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0] as StatusTab | undefined;
                if (key) handleTabChange(key);
              }}
            >
              {tabs.map((tab) => (
                <Toggle
                  key={tab.key}
                  id={tab.key}
                  badge={tab.count != null ? tab.count : undefined}
                >
                  {tab.label}
                </Toggle>
              ))}
            </ToggleButtonGroup>
          ) : undefined
        }
        actions={
          hasAnyLists ? (
            <Button
              onPress={handleCreateClick}
              variant="solid"
              color="primary"
              icon={<LucidePlusCircle />}
            >
              {t.pricing.customPriceLists.list.createButton}
            </Button>
          ) : undefined
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<CustomPriceListRecord>
          isLoading={loadingState?.isInitialLoading || isFetching}
          columns={columns}
          columnVisibilityState={columnVisibilityState}
          dataSource={filteredLists}
          rowKey="id"
          aria-label={t.pricing.customPriceLists.list.ariaLabel}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          persistColumnOrderKey="brokerage:custom-price-lists"
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.common.errors.loading(
                    t.pricing.customPriceLists.entityName,
                    error?.message ?? t.common.errors.unknown,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <CustomPriceListDraftDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => {
          refetch?.();
        }}
      />

      <DuplicateCustomPriceListDialog duplicate={duplicate} />
    </>
  );
};
